import { ChatChannelContext } from './ChatChannel';
import zod from 'zod';
import { useCallback, useReducer, useEffect, useContext } from 'react';
import { RichText, Root } from '@aidkitorg/types/lib/survey';
import { crawlSurveyKinds } from '@aidkitorg/types/lib/traverse';
import { findComponent, TreeBlock } from '../ConfigSearch';

export type QuestionState = {
  sectionId: string,
  visible: boolean,
  content: string,
  type: string,
  targetField: string,
  required: boolean,
  options?: {
    name: string,
    content: RichText,
    terminatesApplication?: boolean,
    fieldType: 'custom-option' | 'select-all' | 'other',
  }[],
  completed?: boolean,
  validity?: {
    state: 'unentered' | 'valid' | 'invalid',
    message?: string
  },
  isNext?: boolean,
  value?: string
};

export type SectionState = {
  name: string,
  complete?: boolean,
  visitable?: boolean,
  viewing?: boolean
};

export type QuestionReducerState = {
  sections: Record<string, SectionState>,
  questions: Record<string, QuestionState>
}

export type QuestionStateEvent = {
  type: 'change',
  questionId: string,
  update: Partial<QuestionState>
} | {
  type: 'update-section',
  sectionId: string,
  update: Partial<SectionState>
}

export function useQuestionStateReducer(initial?: QuestionReducerState) {
  const chat = useContext(ChatChannelContext);

  const [state, dispatch] = useReducer((state: QuestionReducerState, event: QuestionStateEvent) => {
    switch (event.type) {
      case 'change':
        return {
          ...state,
          questions: {
            ...state.questions,
            [event.questionId]: {
              ...(state.questions[event.questionId] ?? {}),
              ...event.update
            }
          }
        };
      case 'update-section':
        return {
          ...state,
          sections: {
            ...state.sections,
            [event.sectionId]: {
              ...(state.sections[event.sectionId] ?? {}),
              ...event.update
            }
          }
        };
      default:
        return state;
    }
  }, initial ?? { questions: {}, sections: {} });

  const questionChanged = useCallback((event: QuestionStateEvent) => {
    dispatch(event);
    if (chat.enabled && chat.channelIsReady) {
      chat.sendChatMessage('section and question state has changed', { role: 'system' });
    }
  }, [dispatch, chat.enabled, chat.channelIsReady]);

  useEffect(() => {
    if (chat.registerTool && chat.enabled && chat.channelIsReady) {
      chat.registerTool({
        name: 'list_sections',
        description: `
          lists the sections that are part of the survey, and their current state.
          use this tool often, as section state changes frequently.

          ## Example Questions that this should be used to answer
            - What questions are in this section?
            - What sections are left to do?
            - How many sections are there, and what are they?
        `,
        schema: zod.object({
        }),
        async fn({ }) {
          return Object.values(state.sections);
        }
      });
      chat.registerTool({
        name: 'list_questions',
        description: `
          list questions and their current state in the survey. questions are not returned in any particular ordering.
          question state also includes the answer given, if one has already been provided. 
          use this tool often, as question state changes frequently.

          ## Example Questions that this should be used to answer
            - Q: What did I answer for [question]?
              Params: {}
            - Q: What is my [question target field] (e.g. email, phone, name)?
              Params: {}
            - Q: What questions have I answered?
              Params: { completionStatus: 'complete' }
            - Q: Why is this saying invalid?
              Params: { visibleOnly: true }
            - Q: the question looks wrong
              Params: { visibleOnly: true }
            - what does this mean? (then ask for clarification)
              Params: { visibleOnly: true }
        `,
        schema: zod.object({
          requiredToAnswerOnly: zod.boolean({
            description: 'if true, returns only questions that still need to be answered'
          }),
          visibleOnly: zod.boolean({
            description: `
            if true, only returns questions that are currently visible on the user's screen.
          ` }),
          completionStatus: zod.nullable(zod.enum(['complete', 'incomplete'])),
        }),
        async fn({ requiredToAnswerOnly, visibleOnly, completionStatus }) {
          return Object.values(state.questions).filter(q =>
            (!requiredToAnswerOnly || q.required) &&
            (!visibleOnly || q.visible) &&
            (!completionStatus || (q.completed && completionStatus === 'complete') || (!q.completed && completionStatus === 'incomplete'))
          ).map(({ sectionId, ...fields }) => ({ ...fields, section: state.sections[sectionId]?.name }));
        }
      });
      chat.registerTool({
        name: 'get_next_question',
        description: `
          gets the state of the next question that needs to be answered in the survey, and what section it is a part of.

          ## Example Questions that this should be used to answer
            - which option should I choose?
            - help me understand more about this question
        `,
        schema: zod.object({
        }),
        async fn({ }) {
          const next = Object.values(state.questions).find(q => q.isNext);
          if(next) {
            return {
              section: state.sections[next.sectionId],
              ...next
            };
          } else {
            return { result: 'no next question' };
          }
        }
      });
      chat.registerTool({
        name: 'get_question_counts',
        description: `
          gathers counts using the state of the survey. 
        `,
        schema: zod.object({
        }),
        async fn({ }) {
          const questions = Object.values(state.questions);
          return questions.reduce((counts, q) => ({
            completed: counts.completed + Number(!!q.completed),
            remaining: counts.remaining + Number(!!q.required),
            total: counts.total + 1,
            visible: counts.visible + Number(!!q.visible)
          }), { completed: 0, total: 0, remaining: 0, visible: 0 });
        }
      });
    }

  }, [chat.enabled, chat.channelIsReady, chat.registerTool, state.sections, state.questions]);

  return {
    questionChanged
  };
}

export function useDistroTools(props: { survey: Root, channel: React.ContextType<typeof ChatChannelContext> }) {

  useEffect(() => {
    if (props.channel.registerTool) {
      console.debug('initializing distro chat tools...');

      props.channel.registerTool({
        name: 'list_active_distro_kinds',
        description: `
          list all of the distro kinds utilized in the current configuration.
            
          ## Examples
          
          ### Asking Plainly for Kind Usage
          User: what kinds are used in this survey?
          Tool Call Args: {}
          
        `,
        schema: zod.object({}),
        async fn() {
          const kinds = new Set<string>();
          crawlSurveyKinds(props.survey, item => item.kind && kinds.add(item.kind));
          return Array.from(kinds);
        }
      });

      props.channel.registerTool({
        name: 'find_kind',
        description: `
        search within the configuration for a specific distro kind.

        ## Examples
        
        User: please find all payments
        Tool Call Args: { "searchTerm": "payment", "limitToKind": true }
      `,
        schema: zod.object({
          kind: zod.string({ description: 'the kind to search for' }),
        }),
        async fn({ kind }) {
          let survey = props.survey;
          if (!Array.isArray(survey)) {
            survey = survey.survey;
          }

          const components = (await findComponent(survey, kind, {
            includeDashboards: true,
            wholeMatchOnly: false,
            matchCase: false
          }))
            ?.filter(c => c.thing.kind.toLowerCase() === kind.toLowerCase())
            ?.map(c => JSON.parse(JSON.stringify(c.thing,
              (_, value) => typeof value === 'object' && !Array.isArray(value) && 'en' in value ? value['en'] : value)
            ))

          return components;
        }
      });

      props.channel.registerTool({
        name: 'find_by_keyword',
        description: `
        search within the configuration for a specific keyword. this should be the last thing used, other tools are more specific.

        ## Examples
        
        ### Searching for Target Fields
        User: where is payment_1_ok used
        Tool Call Args: { "searchTerm": "payment_1_ok", "matchCase": true, "wholeMatchOnly": true, "includeDashboards": true }
      `,
        schema: zod.object({
          searchTerm: zod.string({ description: 'the term to search with' }),
          includeDashboards: zod.boolean({ description: 'include dashboard types in the search' }),
          wholeMatchOnly: zod.boolean({ description: 'match the whole search term exactly' }),
          matchCase: zod.boolean({ description: 'filter matches to the exact casing of the search term' }),
        }),
        async fn({ searchTerm, includeDashboards, wholeMatchOnly, matchCase }) {
          let survey = props.survey;
          if (!Array.isArray(survey)) {
            survey = survey.survey;
          }
          let components: TreeBlock[] = await findComponent(survey, searchTerm, {
            includeDashboards,
            wholeMatchOnly,
            matchCase
          }) ?? [];

          return components;
        }
      })
    }
  }, [props.survey, props.channel.registerTool]);
}
